import { backgroundTransitionOnClick, Empty, IconFont, Input, pointer, Popover, Text, tinyButtons } from '@byecode/ui'
import type { DTFile, ValueVariable, VariableFieldADTValue } from '@lighthouse/core'
import { AppUserStatus, type Field, type VariableADTvalue, VariableType } from '@lighthouse/core'
import { isValid } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { CURRENT_USER, dateUnitToMode, inputFieldTypes } from '../../../../constants'
import { useApplicationContext } from '../../../../contexts'
import type { FetchPexelsParams, PexelsResponse } from '../../../../types'
import { getDateConfig, getRealField, isDateValue, isIdsValue, isStringArray } from '../../../../utils'
import type { UploadDropZoneProps } from '../../../'
import {
    getMultipleValue,
    TagItemPreviewer,
    TooltipText,
    UploadDropZone,
    useUploadBatchError,
    useUploadBatchFinished,
    useUploadBatchProgress,
    useUploadBatchUploading
} from '../../../'
import { CheckboxItemPreviewer } from '../../../FieldValuePreviewer/CheckboxValuePreviewer'
import { SelectItemPreviewer } from '../../../FieldValuePreviewer/SelectValuePreviewer'
import { AggregationInnerTypeToFieldType, ignoreEleId, variableFieldInfoMap } from '../../constant'
import { getEmptyFieldVariableValue } from '../../help'
import DateCalendar from './CustomCalendar'
import { CustomDepartments } from './CustomDepartment'
import CustomPexels from './CustomPexels'

interface VariableCustomProps {
    field?: Field
    isMultiple?: boolean
    disabledPexels?: boolean
    value?: ValueVariable
    uploadProps?: Pick<UploadDropZoneProps, 'uploadOptions' | 'accept' | 'multiple'>
    opened: boolean
    onOpenChange: (v: boolean) => void
    onChange?: (value: VariableADTvalue) => void
    onClose?: () => void
    onChangeFileProcess?: (v: number) => void
    onFetchPexelsList?: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

const SCxLeftFill = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    gap: 6px;
    height: 100%;
    font-size: 14px;
    overflow: hidden;
`

const SCxContainer = styled.div``

const SCxRightFill = styled.div`
    flex-shrink: 0;
`

const SCxListContainer = styled.div`
    height: auto;
    max-height: 600px;
    overflow: hidden auto;
    min-width: 200px;
    max-width: 500px;
    ${tinyButtons}
`

const SCxListItem = styled.div`
    width: 100%;
    height: 36px;
    padding: 0 16px;
    gap: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: var(--font-size-normal);
    overflow: hidden;
    ${backgroundTransitionOnClick}
    ${pointer}
`

const SCxTagIcon = styled(IconFont)<{ bgColor?: string }>`
    padding: 2px;
    border-radius: 2px;
    background-color: ${({ bgColor }) => bgColor};
`
export const VariableCustom: React.FunctionComponent<VariableCustomProps> = ({
    value: data,
    field,
    isMultiple,
    uploadProps,
    disabledPexels,
    opened,
    onOpenChange: setOpened,
    onChange,
    onClose,
    onChangeFileProcess,
    onFetchPexelsList
}) => {
    const realField = useMemo(() => (field ? getRealField(field) : field), [field])

    // const [opened, setOpened] = useState(false)
    const inputType = useMemo(() => {
        if (realField?.type === 'aggregation') {
            return AggregationInnerTypeToFieldType[realField?.innerType || 'NULL']
        }
        return realField?.type ?? 'text'
    }, [realField?.innerType, realField?.type])
    const isTextField = inputFieldTypes.has(inputType)
    const [searchWord, setSearchWord] = useState('')
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()

    const personList = personOptions.filter(item => item.status !== AppUserStatus.DEPART)

    const initValue = useMemo(
        () => (data?.type === VariableType.VALUE ? (isIdsValue(data?.valueVariable?.value) ? data.valueVariable?.value : []) : []),

        [data]
    )
    const uploadId = uploadProps?.uploadOptions?.info.id ?? ''

    const [loading, setLoading] = useState(false)

    const fieldType = useMemo(() => (field?.type === 'video' ? 'video' : 'file'), [field?.type])

    useUploadBatchUploading(uploadId, batchList => {
        setLoading(true)
        const progress = Math.max(
            ...batchList.map(item => {
                const completed = Number(item?.completed ?? 0)
                return Number.isFinite(completed) ? completed : 0
            }),
            0
        )
        onChangeFileProcess?.(progress)
    })

    useUploadBatchProgress(uploadId, batchList => {
        setLoading(true)
        const progress = Math.max(
            ...batchList.map(item => {
                const completed = Number(item?.completed ?? 0)
                return Number.isFinite(completed) ? completed : 0
            }),
            0
        )
        onChangeFileProcess?.(progress)
    })

    useUploadBatchFinished(uploadId, batchItems => {
        const fileList: DTFile[] = batchItems
            .map(batchItem => {
                const {
                    file: { name, size },
                    uploadResponse: {
                        data: { content, success }
                    }
                } = batchItem
                return content?.url
            })
            .filter(Boolean)

        setLoading(false)
        onChangeFileProcess?.(-1)
        if (fileList.length > 0) {
            onChange?.({
                type: VariableType.VALUE,
                valueVariable: { type: fieldType, value: fileList }
            })
            onClose?.()
        }
    })

    useUploadBatchError(uploadId, batchItems => {
        setLoading(false)
        onChangeFileProcess?.(-1)
        onClose?.()
    })

    const handleChange = useCallback(
        (value: VariableFieldADTValue, isMultiple?: boolean) => {
            !isMultiple && onClose?.()
            onChange?.({ type: VariableType.VALUE, valueVariable: value })
        },
        [onChange, onClose]
    )

    const contentEle = useMemo(() => {
        switch (inputType) {
            case 'id':
            case 'text':
            case 'phoneNumber':
            case 'email':
            case 'number': {
                return (
                    <SCxListItem>
                        <SCxLeftFill
                            onClick={() => {
                                handleChange(getEmptyFieldVariableValue(inputType))
                            }}
                        >
                            <IconFont color="var(--color-gray-400)" size={16} type="InputIcon" />
                            <Text size={14} style={{ lineHeight: 1 }}>
                                输入具体值
                            </Text>
                        </SCxLeftFill>
                        <SCxRightFill>
                            {data?.type === VariableType.VALUE && <IconFont size={16} type="Tick" color="var(--color-purple-500)" />}
                        </SCxRightFill>
                    </SCxListItem>
                )
            }
            case 'url': {
                return (
                    <SCxListItem>
                        <SCxLeftFill
                            onClick={() => {
                                handleChange(getEmptyFieldVariableValue('url'))
                            }}
                        >
                            <IconFont color="var(--color-gray-400)" size={16} type="InputIcon" />
                            <Text size={14} style={{ lineHeight: 1 }}>
                                输入链接
                            </Text>
                        </SCxLeftFill>
                        <SCxRightFill>
                            {data?.type === VariableType.VALUE && <IconFont size={16} type="Tick" color="var(--color-purple-500)" />}
                        </SCxRightFill>
                    </SCxListItem>
                )
            }
            case 'video': {
                return (
                    <>
                        <SCxListItem
                            onClick={() => {
                                onClose?.()
                                handleChange(getEmptyFieldVariableValue('file'))
                            }}
                        >
                            <SCxLeftFill>
                                <IconFont color="var(--color-gray-400)" size={16} type="PropertyLink" />
                                <Text size={14} style={{ lineHeight: 1 }}>
                                    视频链接
                                </Text>
                            </SCxLeftFill>
                        </SCxListItem>
                        {uploadProps && (
                            <UploadDropZone {...uploadProps}>
                                <SCxListItem
                                    onClick={e => {
                                        setTimeout(() => setOpened(false), 0)
                                    }}
                                >
                                    <SCxLeftFill>
                                        <IconFont color="var(--color-gray-400)" size={16} type="UploadSimple" />
                                        <Text size={14} style={{ lineHeight: 1 }}>
                                            从本地上传
                                        </Text>
                                    </SCxLeftFill>
                                </SCxListItem>
                            </UploadDropZone>
                        )}
                    </>
                )
            }
            case 'file': {
                if (!uploadProps && disabledPexels) {
                    return (
                        <SCxListItem>
                            <SCxLeftFill
                                onClick={() => {
                                    handleChange(getEmptyFieldVariableValue(inputType))
                                }}
                            >
                                <IconFont color="var(--color-gray-400)" size={16} type="InputIcon" />
                                <Text size={14} style={{ lineHeight: 1 }}>
                                    输入具体值
                                </Text>
                            </SCxLeftFill>
                            <SCxRightFill>
                                {data?.type === VariableType.VALUE && <IconFont size={16} type="Tick" color="var(--color-purple-500)" />}
                            </SCxRightFill>
                        </SCxListItem>
                    )
                }
                return (
                    <>
                        {uploadProps && (
                            <div
                                onClick={e => {
                                    // 处理点击本地上传时打开了pexels
                                    setTimeout(() => setOpened(false), 0)
                                }}
                            >
                                <UploadDropZone {...uploadProps}>
                                    <SCxListItem>
                                        <SCxLeftFill>
                                            <IconFont color="var(--color-gray-400)" size={16} type="UploadSimple" />
                                            <Text size={14} style={{ lineHeight: 1 }}>
                                                从本地上传
                                            </Text>
                                        </SCxLeftFill>
                                    </SCxListItem>
                                </UploadDropZone>
                            </div>
                        )}
                        {!disabledPexels && (
                            <SCxListItem>
                                <SCxLeftFill>
                                    <SCxTagIcon color="var(--color-white)" size={12} type="Pexels" bgColor="#05A081" />
                                    <Text size={14} style={{ lineHeight: 1 }}>
                                        Pexels
                                    </Text>
                                </SCxLeftFill>
                                <SCxRightFill>
                                    <IconFont type="ArrowRightSmall" color="var(--color-gray-400)" size={16} />
                                </SCxRightFill>
                            </SCxListItem>
                        )}
                    </>
                )
            }

            case 'notes': {
                return null
            }

            default: {
                return (
                    <SCxListItem>
                        <SCxLeftFill>
                            <IconFont size={16} color="var(--color-gray-400)" type={variableFieldInfoMap[inputType].icon} />
                            <Text>{variableFieldInfoMap[inputType].name} </Text>
                        </SCxLeftFill>
                        <SCxRightFill>
                            {data?.type === VariableType.VALUE && <IconFont size={16} type="Tick" color="var(--color-purple-500)" />}
                            <IconFont type="ArrowRightSmall" color="var(--color-gray-400)" size={16} />
                        </SCxRightFill>
                    </SCxListItem>
                )
            }
        }
    }, [data?.type, disabledPexels, handleChange, inputType, onClose, setOpened, uploadProps])

    const listEle = useMemo(() => {
        if (!field) {
            return null
        }
        switch (inputType) {
            case 'select': {
                if (field.type === 'select') {
                    const selectMultiple = field.type === 'select' && field?.select?.multiple !== undefined ? field.select.multiple : true
                    const isCurrentMultiple = isMultiple ?? selectMultiple
                    const { options } = field.select

                    const lowerCaseSearch = searchWord.toLocaleLowerCase()
                    const newOptions = options.filter(
                        v => v.id.toLocaleLowerCase().includes(lowerCaseSearch) || v.label.toLocaleLowerCase().includes(lowerCaseSearch)
                    )
                    return (
                        <>
                            <div style={{ padding: '0 12px  8px 12px' }}>
                                <Input value={searchWord} onChange={e => setSearchWord(e.target.value)} />
                            </div>
                            {newOptions.map(item => {
                                return (
                                    <SCxListItem
                                        key={item.label}
                                        onClick={() =>
                                            isCurrentMultiple
                                                ? handleChange(
                                                      { type: inputType, value: getMultipleValue(initValue ?? [], item.label) },
                                                      isCurrentMultiple
                                                  )
                                                : handleChange({ type: inputType, value: [item.label] })
                                        }
                                    >
                                        <SelectItemPreviewer id="" data={item} />
                                        <SCxRightFill>
                                            {initValue?.includes(item.label) && (
                                                <IconFont color="var(--color-purple-500)" type="Tick" size={16} />
                                            )}
                                        </SCxRightFill>
                                    </SCxListItem>
                                )
                            })}
                            {newOptions.length === 0 && <Empty icon="Nodata-8i554976" description="暂无可选项" />}
                        </>
                    )
                }
                return null
            }

            case 'date': {
                const dateValue =
                    data?.type === VariableType.VALUE
                        ? isDateValue(data?.valueVariable?.value)
                            ? new Date(data?.valueVariable?.value ?? '')
                            : new Date()
                        : new Date()

                const finallyDateVal = isValid(dateValue) ? dateValue : undefined
                const { unit, format } = getDateConfig(field) || {}
                const calendarMode = dateUnitToMode[unit]

                return <DateCalendar value={finallyDateVal} mode={calendarMode} config={{ format }} onChange={handleChange} />
            }

            case 'checkbox': {
                return (
                    <>
                        <SCxListItem onClick={() => handleChange({ type: inputType, value: true })}>
                            <SCxLeftFill>
                                <CheckboxItemPreviewer id="" data />
                                <Text>是</Text>
                            </SCxLeftFill>
                        </SCxListItem>
                        <SCxListItem onClick={() => handleChange({ type: inputType, value: false })}>
                            <SCxLeftFill>
                                <CheckboxItemPreviewer id="" data={false} />
                                <SCxRightFill>否</SCxRightFill>
                            </SCxLeftFill>
                        </SCxListItem>
                    </>
                )
            }
            case 'file': {
                return <CustomPexels onChange={handleChange} onFetchPexelsList={onFetchPexelsList} />
            }

            case 'person': {
                const lowerCaseSearch = searchWord.toLocaleLowerCase()
                const newPersonOptions = [CURRENT_USER, ...personList].filter(
                    v => v.id.toLocaleLowerCase().includes(lowerCaseSearch) || v.name.toLocaleLowerCase().includes(lowerCaseSearch)
                )
                const isCurrentMultiple = isMultiple ?? (field.type === 'person' && field.person.multiple)
                return (
                    <>
                        <div style={{ padding: '0 12px  8px 12px' }}>
                            <Input value={searchWord} onChange={e => setSearchWord(e.target.value)} />
                        </div>
                        {newPersonOptions.map(item => {
                            return (
                                <SCxListItem
                                    key={item.id}
                                    onClick={() =>
                                        isCurrentMultiple
                                            ? handleChange(
                                                  { type: inputType, value: getMultipleValue(initValue ?? [], item.id) },
                                                  isCurrentMultiple
                                              )
                                            : handleChange({ type: inputType, value: [item.id] })
                                    }
                                >
                                    <TagItemPreviewer data={item} isHiddenUserId />
                                    <SCxRightFill>
                                        {initValue?.includes(item.id) && (
                                            <IconFont type="Tick" color="var(--color-purple-500)" size={16} />
                                        )}
                                    </SCxRightFill>
                                </SCxListItem>
                            )
                        })}
                        {newPersonOptions.length === 0 && <Empty description="暂无可选人员" icon="Nodata-8i554976" />}
                    </>
                )
            }
            case 'role': {
                const lowerCaseSearch = searchWord.toLocaleLowerCase()
                const newRoleOptions = roleOptions.filter(
                    v => v.id.toLocaleLowerCase().includes(lowerCaseSearch) || v.name.toLocaleLowerCase().includes(lowerCaseSearch)
                )
                return (
                    <>
                        <div style={{ padding: '0 12px  8px 12px' }}>
                            <Input value={searchWord} onChange={e => setSearchWord(e.target.value)} />
                        </div>
                        {newRoleOptions.map(item => {
                            return (
                                <SCxListItem key={item.id} onClick={() => handleChange({ type: inputType, value: [item.id] })}>
                                    <SCxLeftFill>
                                        <TooltipText title={item.name} render={ref => <Text ref={ref}>{item.name}</Text>} />
                                    </SCxLeftFill>
                                    <SCxRightFill>
                                        {initValue?.includes(item.id) ? (
                                            <IconFont type="Tick" color="var(--color-purple-500)" size={16} />
                                        ) : (
                                            <Text color="var(--color-gray-400)">{item.id}</Text>
                                        )}
                                    </SCxRightFill>
                                </SCxListItem>
                            )
                        })}
                        {newRoleOptions.length === 0 && <Empty icon="Nodata-8i554976" description="暂无可选角色" />}
                    </>
                )
            }
            case 'parentDepartment':
            case 'department': {
                const value =
                    data?.type === VariableType.VALUE ? (isStringArray(data?.valueVariable?.value) ? data?.valueVariable?.value : []) : []
                return (
                    <CustomDepartments
                        multiple={field.type === 'department'}
                        value={value}
                        onChange={v =>
                            onChange?.({ type: VariableType.VALUE, valueVariable: { type: field.type, value: v } } as VariableADTvalue)
                        }
                        onClose={() => setOpened(false)}
                    />
                )
            }
            case 'singleSelect': {
                const options = field.type === 'singleSelect' ? field.singleSelect.options : []
                return (
                    <>
                        {options.map(item => {
                            return (
                                <SCxListItem key={item.id} onClick={() => handleChange({ type: inputType, value: [item.label] })}>
                                    <SCxLeftFill>
                                        <TooltipText title={item.label} render={ref => <Text ref={ref}>{item.label}</Text>} />
                                    </SCxLeftFill>
                                    <SCxRightFill>
                                        {initValue?.includes(item.label) && (
                                            <IconFont type="Tick" color="var(--color-purple-500)" size={16} />
                                        )}
                                    </SCxRightFill>
                                </SCxListItem>
                            )
                        })}
                    </>
                )
            }
            default: {
                return null
            }
        }
    }, [
        data?.type,
        data?.valueVariable?.value,
        field,
        handleChange,
        initValue,
        inputType,
        isMultiple,
        onChange,
        onFetchPexelsList,
        personList,
        roleOptions,
        searchWord,
        setOpened
    ])

    return contentEle ? (
        <Popover
            opened={opened}
            onChange={setOpened}
            disabled={isTextField || fieldType === 'video'}
            width="auto"
            position="left-start"
            // withinPortal
            closeClickOutside
        >
            <Popover.Target>
                <SCxContainer>{contentEle}</SCxContainer>
            </Popover.Target>
            <Popover.Dropdown compact={inputType === 'date' || inputType === 'file' || inputType === 'department'}>
                <SCxListContainer id={ignoreEleId}>{listEle}</SCxListContainer>
            </Popover.Dropdown>
        </Popover>
    ) : null
}
